<template>
  <div
    :class="$style.downloadableDocument"
    data-downloadable-document-v="3.2.4"
  >
    <BasePicture
      :src="`${data?.fields?.image?.fields?.file?.url}?w=74&q=70`"
      :alt="data?.fields?.associatedBrand?.fields?.label || ''"
      :img-class="$style.logo"
      :class="$style.picture"
      aria-hidden="true"
    />
    <div :class="$style.title">
      <div :class="$style.titleText">{{ data?.fields?.title }}</div>
    </div>
    <div :class="$style.updated">{{ computedUpdated }}</div>
    <div :class="$style.downloadWrapper">
      <BaseButton
        v-if="data?.fields?.signInToDownload && !loggedIn"
        presentation="light"
        size="default"
        @click="handleLogin"
      >
        {{ localizedStrings['sign-in-to-download'] || 'Sign In to Download' }}
      </BaseButton>
      <a
        v-else
        :href="computedCta?.fields?.link || ''"
        target="_blank"
        :class="$style.cta"
        :event-action="computedCta?.eventAction"
        :event-category="computedCta?.eventCategory"
        :event-label="computedCta?.eventLabel"
        :event-title="computedCta?.entryTitle"
        :data-entry="data?.sys?.id"
        :data-brand="computedAssociatedBrandValue"
        :data-product="computedAssociatedProductValue"
        rel="noopener noreferrer"
        @click="handleClick"
      >
        {{ computedCta?.fields?.label || 'Download' }}
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatLocalizedDate } from '../utils/localizeDates'

import type { TypeDownloadableDocumentWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { handleLogin } = useAuthLogin()
const { locale } = useI18n()
const userStore = useUserStore()
const { loggedIn } = storeToRefs(userStore)

const { localizedStrings } = useLocalizedStrings()

// Props
const props = defineProps<{
  data: TypeDownloadableDocumentWithoutUnresolvableLinksResponse
  setName?: string
}>()

// Computed
const computedUpdated = computed(() => {
  const updated = props?.data?.fields?.updated
  return updated ? formatLocalizedDate(updated, 'PP', locale.value) : ''
})

const computedAssociatedBrandValue = computed(
  () =>
    props.data?.fields?.associatedBrand?.fields?.value ||
    props.data?.fields?.brand2?.fields?.value ||
    props.data?.fields?.brand ||
    '',
)

const computedAssociatedProduct = computed(
  () => props.data?.fields?.associatedProduct,
)
const computedAssociatedProductValue = computed(
  () => computedAssociatedProduct.value?.fields?.entryTitle || '',
)

const computedCta = computed(() => {
  if (!props?.data?.fields?.cta) return

  return {
    eventAction: 'click',
    eventCategory: 'Download',
    eventLabel: props?.data?.fields?.cta?.fields?.link,
    ...props?.data?.fields?.cta,
    entryTitle: props?.data?.fields?.entryTitle,
    fields: {
      ...props?.data?.fields?.cta?.fields,
      target: '_blank' as const,
    },
  }
})

function handleClick() {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'ga4.trackEvent',
    eventName: 'resource_download',
    eventParams: {
      file_name: computedCta.value?.fields?.link
        ? new URL(computedCta.value.fields.link, 'https://placeholder').pathname
        : '',
      file_extension: computedCta.value?.fields?.link
        ?.split('/')
        ?.pop()
        ?.split('.')
        ?.pop()
        ?.toLowerCase(),
      link_text: computedCta.value?.fields?.label || '',
      link_url: computedCta.value?.fields?.link || '',
      resource_type: 'rules & documentation',
      resource_name: props.data?.fields?.entryTitle,
      set_name: props.setName || '',
      associated_brand: computedAssociatedBrandValue.value,
      associated_product: computedAssociatedProductValue.value,
    },
  })
}
</script>

<style lang="scss" module>
.downloadableDocument {
  align-items: center;
  background-color: var(--gray-8);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border: 1px solid var(--gray-7);
  color: var(--white);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 94px;
  height: min-content;
  justify-content: flex-start;
  overflow: hidden;
  padding: 10px;
  width: 100%;
  font-size: inherit;
  font-family: inherit;
  text-align: center;

  @include media('sm') {
    display: grid;
    grid-template-columns: 200px 1fr 0.5fr 0.5fr;
    text-align: left;
  }
}

.picture {
  align-items: center;
  display: flex;
  flex: 0 0 72px;
  height: 100%;
  max-width: 72px;
  max-height: 72px;
  text-align: center;

  @include media('sm') {
    flex: 0 0 200px;
    justify-content: center;
    max-width: 200px;
  }
}

.logo {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.titleText {
  font-weight: 700;
  flex: 1 1 auto;

  @include media('sm') {
    flex: 0 0 auto;
    margin-left: 1.5em;
  }
}

.updated {
  text-transform: uppercase;

  @include media('sm') {
    margin-left: 1.5em;
  }
}

.cta {
  background: var(--purple);
  border-radius: 5px;
  border: var(--button-border);
  transition: cubic-bezier(0.16, 1, 0.29, 0.99) 0.2s;

  color: var(--white);
  font-family: var(--roboto-condensed);
  font-size: 18px;
  font-weight: bold;
  text-decoration: none;

  display: inline-flex;
  justify-content: center;

  padding: 0.5em 1.45em;
  min-width: 150px;
  outline: none; // remove browser default focus state

  &:hover,
  &:focus {
    background: var(--purple-light);
  }

  &:active {
    background: var(--purple-dark);
  }

  // remove browser default focus state
  &::-moz-focus-inner {
    border: none;
  }
}

.downloadWrapper {
  margin: 0 auto;
}
</style>
